import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SocialService } from './social.service';
import {
  CatchHeatData, DEFAULT_WINDOW_DAYS, EMPTY_CATCH_HEAT, parseCatchHeat,
} from '../shared/utils/catch-heat.util';

export type CatchHeatStatus = 'idle' | 'loading' | 'ok' | 'empty' | 'error';

export interface CatchHeatState {
  enabled: boolean;
  window: number;
  species: string;
  status: CatchHeatStatus;
  data: CatchHeatData;
}

/** The fleet whose members the backend serves catch bundles to (Flutter `_kCatchHeatFleetName`). */
const CATCH_HEAT_FLEET = 'waterproof intel service';

const normalize = (v: unknown): string => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Catch-heat data + selection state. The backend is the authority on the data (a non-member gets
 * the empty envelope); fleet membership here only decides whether the tile is offered at all,
 * fail-closed until the fleet list lands.
 */
@Injectable({ providedIn: 'root' })
export class CatchHeatService {
  private readonly stateSubject = new BehaviorSubject<CatchHeatState>({
    enabled: false, window: DEFAULT_WINDOW_DAYS, species: 'all', status: 'idle', data: EMPTY_CATCH_HEAT,
  });
  readonly state$: Observable<CatchHeatState> = this.stateSubject.asObservable();
  readonly enabled$: Observable<boolean> = this.state$.pipe(map((s) => s.enabled), distinctUntilChanged());

  private readonly memberSubject = new BehaviorSubject<boolean>(false);
  /** Whether the heat tile is offered: member of the heat fleet. */
  readonly isMember$: Observable<boolean> = this.memberSubject.asObservable();
  private membershipRequested = false;

  /** Bumped per fetch so a slow response can't overwrite a newer window/species pick. */
  private fetchSeq = 0;

  constructor(private http: HttpClient, private social: SocialService) {}

  get state(): CatchHeatState {
    return this.stateSubject.value;
  }

  get isMember(): boolean {
    return this.memberSubject.value;
  }

  /** Load fleet membership once per session (the live list is authoritative). */
  ensureMembership(): void {
    // Local-only override (environment.local.ts) to offer the tile without fleet membership.
    if ((environment as { forceCatchHeat?: boolean }).forceCatchHeat) {
      this.memberSubject.next(true);
      return;
    }
    if (this.membershipRequested) return;
    this.membershipRequested = true;
    this.social.myFleets(1, 100).subscribe({
      next: (res: any) => {
        const fleets = Array.isArray(res?.results) ? res.results
          : Array.isArray(res?.data?.results) ? res.data.results
            : Array.isArray(res?.data) ? res.data
              : Array.isArray(res) ? res : [];
        this.memberSubject.next(fleets.some((f: any) =>
          normalize(f?.name || f?.community?.name || f?.community_name || f?.display_name) === CATCH_HEAT_FLEET));
      },
      // Fail closed, and allow a retry on the next open.
      error: () => { this.membershipRequested = false; },
    });
  }

  enable(): void {
    if (this.state.enabled) return;
    this.patch({ enabled: true });
    void this.fetch();
  }

  /** Off: drop the loaded data (the layer must go blank), keep the user's window/species pick. */
  disable(): void {
    this.fetchSeq++;
    this.patch({ enabled: false, status: 'idle', data: EMPTY_CATCH_HEAT });
  }

  setWindow(window: number): void {
    if (window === this.state.window) return;
    this.patch({ window });
    void this.fetch();
  }

  setSpecies(species: string): void {
    if (species === this.state.species) return;
    this.patch({ species });
    void this.fetch();
  }

  retry(): void {
    void this.fetch();
  }

  private async fetch(): Promise<void> {
    const seq = ++this.fetchSeq;
    const { window, species } = this.state;
    this.patch({ status: 'loading' });
    try {
      const params = new HttpParams().set('window', String(window)).set('species', species);
      const json = await firstValueFrom(this.http.get(`${environment.apiUrl}/waypoint/heat/`, { params }));
      if (seq !== this.fetchSeq || !this.state.enabled) return;
      const data = parseCatchHeat(json);
      // A served window that isn't offered any more (bundle rebuilt) snaps to one that is.
      const snapped = data.availableWindows.includes(this.state.window)
        ? this.state.window : data.availableWindows[data.availableWindows.length - 1];
      const hasAnything = data.hotspots.length + data.density.length + data.pins.length > 0;
      this.patch({ data, window: snapped, status: hasAnything ? 'ok' : 'empty' });
    } catch {
      if (seq !== this.fetchSeq) return;
      this.patch({ status: 'error' });
    }
  }

  private patch(change: Partial<CatchHeatState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...change });
  }
}
